import { useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCcw, Trash2, X } from "lucide-react";

export type RoomAction = "reset" | "delete";

export function RoomActionsModal({
  roomName,
  onClose,
  onReset,
  onDelete
}: {
  roomName: string;
  onClose: () => void;
  onReset: () => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [confirming, setConfirming] = useState<RoomAction | null>(null);
  const [busy, setBusy] = useState(false);

  async function confirm() {
    if (!confirming) return;
    setBusy(true);
    try {
      if (confirming === "reset") await onReset();
      else await onDelete();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !busy) onClose();
    }}>
      <div role="dialog" aria-modal="true" aria-labelledby="room-actions-title" className="w-full max-w-md rounded-t-2xl border bg-card p-4 shadow-2xl sm:rounded-2xl sm:p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="room-actions-title" className="font-semibold">Chat actions</h2>
            <p className="mt-1 truncate text-xs text-muted-foreground">{roomName}</p>
          </div>
          <Button aria-label="Close chat actions" variant="ghost" size="icon" onClick={onClose} disabled={busy}><X /></Button>
        </div>

        {confirming ? (
          <div className="mt-5 rounded-xl border border-amber-400/30 bg-amber-950/25 p-4">
            <div className="flex gap-3">
              <AlertTriangle className="mt-0.5 shrink-0 text-amber-300" />
              <div>
                <h3 className="font-medium">{confirming === "reset" ? "Reset this chat?" : "Delete this chat?"}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {confirming === "reset"
                    ? "All sent messages, replies, summaries, and saved memories will be permanently removed. The room stays available."
                    : "This permanently removes the room, its messages, replies, summaries, and saved memories. This cannot be undone."}
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="ghost" onClick={() => setConfirming(null)} disabled={busy}>Cancel</Button>
              <Button variant={confirming === "delete" ? "destructive" : "default"} onClick={() => void confirm()} disabled={busy}>
                {busy ? "Working..." : confirming === "reset" ? "Reset chat" : "Delete chat"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-2">
            <button className="flex w-full items-start gap-3 rounded-xl border border-border p-3 text-left transition-colors hover:bg-muted/50" onClick={() => setConfirming("reset")}>
              <RefreshCcw className="mt-0.5 shrink-0 text-primary" size={18} />
              <span><span className="block text-sm font-medium">Reset chat</span><span className="mt-1 block text-xs text-muted-foreground">Clear messages and memory, keep this room.</span></span>
            </button>
            <button className="flex w-full items-start gap-3 rounded-xl border border-red-400/20 p-3 text-left transition-colors hover:bg-red-950/30" onClick={() => setConfirming("delete")}>
              <Trash2 className="mt-0.5 shrink-0 text-red-300" size={18} />
              <span><span className="block text-sm font-medium text-red-100">Delete chat</span><span className="mt-1 block text-xs text-red-200/70">Remove this room and everything inside it.</span></span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
